import {
  Box,
  Button,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spacer,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { FiEdit, FiSearch } from 'react-icons/fi'
import { RiDeleteBinFill } from 'react-icons/ri'
import type { Column } from 'react-table'
import { useTable } from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import { checkPermission, PermissionsEnum } from '../../../../utils'
import useToast from '../../../hooks/useToast'
import { UserContext } from '../../../providers'
import PermissionGuard from '../../PermissionGuard'
import DeleteDialog from './DeleteDialog'
import FormModal from '../SavedExtensionsSettings/FormModal'

interface ExtensionPreset {
  id: string
  name: string
  description: string
  config: Record<string, any>
  createdBy: string
  createdAt: string
  updatedAt: string
}

const SavedExtensionsSettings = () => {
  /*
    State variables
  */
  const {
    isOpen: formModalOpen,
    onOpen: openFormModal,
    onClose: closeFormModal,
  } = useDisclosure()
  const {
    isOpen: deleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDisclosure()
  const [currentlyEditingItem, setCurrentlyEditingItem] = useState<string>('')
  const [currentlyDeletingItem, setCurrentlyDeletingItem] = useState<string>('')
  const [searchText, setSearchText] = useState<string>('')

  /*
    Utilities
  */
  const toast = useToast()
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  /*
    API Handling
  */
  const {
    data: getApiResponse,
    request: getExtensionsRequest,
    loading: getLoading,
  } = useFetch<ExtensionPreset[]>('/api/v1/saved-extensions', {
    method: 'get',
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const { request: deleteExtensionRequest, loading: deleteLoading } = useFetch<{
    message: string
  }>('/api/v1/saved-extensions', {
    method: 'delete',
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const getExtensions = async () => {
    const response = await getExtensionsRequest.get()

    if (!response || getExtensionsRequest.error) {
      toast({
        description: t(
          'error_in_fetching_saved_extensions',
          'Error in fetching saved extensions',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
    }

    return
  }

  const deleteExtensionPreset = async () => {
    if (!currentlyDeletingItem) {
      return
    }

    await deleteExtensionRequest.delete(`/${currentlyDeletingItem}`)

    if (deleteExtensionRequest.error) {
      toast({
        description: (deleteExtensionRequest as any)?.data?.message
          ? (deleteExtensionRequest as any).data.message
          : t(
              'error_in_deleting_saved_extension',
              'Error in deleting saved extension.',
            ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }

    toast({
      description: t(
        'saved_extension_deleted_successfully',
        'Saved extension deleted successfully.',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
    void getExtensions()
    return
  }

  /*
    Effects
  */
  useEffect(() => {
    void getExtensions()
  }, [])

  /*
    Memoizations
  */
  const TableHeader = useMemo(
    () =>
      ({
        translationKey,
        mainText,
      }: {
        translationKey: string
        mainText: string
      }): React.ReactElement =>
        (
          <ChakraText color={colors.appDarkIndigo} fontWeight="medium">
            {t(translationKey, mainText)}
          </ChakraText>
        ),
    [],
  )

  const filteredExtensions = useMemo(() => {
    if (!Array.isArray(getApiResponse)) {
      return []
    }

    if (!searchText.trim()) {
      return getApiResponse
    }

    const lowerSearch = searchText.toLowerCase()
    return getApiResponse.filter(
      ext =>
        ext.name.toLowerCase().includes(lowerSearch) ||
        ext.description.toLowerCase().includes(lowerSearch),
    )
  }, [getApiResponse, searchText])

  const reactTableColumns: Array<Column<ExtensionPreset>> = useMemo(() => {
    const hasSettingsWritePermission = checkPermission({
      user,
      permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
    })

    const columns: Array<Column<ExtensionPreset>> = [
      {
        Header: (
          <TableHeader translationKey="saved_extension_name" mainText="Name" />
        ),
        accessor: 'name',
        Cell: ({ row }) => (
          <Tooltip label={row.original.name}>
            <ChakraText isTruncated>{row.original.name}</ChakraText>
          </Tooltip>
        ),
      },
      {
        Header: (
          <TableHeader
            translationKey="saved_extension_description"
            mainText="Description"
          />
        ),
        accessor: 'description',
        Cell: ({ row }) => (
          <Tooltip label={row.original.description}>
            <ChakraText isTruncated>{row.original.description}</ChakraText>
          </Tooltip>
        ),
      },
      {
        id: 'actions',
        Header: <TableHeader translationKey="actions" mainText="Actions" />,
        accessor: () => '',
        Cell: ({ row }) => (
          <Menu>
            <MenuButton
              as={IconButton}
              aria-label="Options"
              icon={<BsThreeDotsVertical />}
              variant="link"
              boxShadow="none"
            />
            <MenuList>
              <MenuItem
                icon={<FiEdit />}
                onClick={() => {
                  setCurrentlyEditingItem(row?.original?.id ?? '')
                  openFormModal()
                }}
              >
                {t('view_edit', 'View/edit')}
              </MenuItem>
              {hasSettingsWritePermission && (
                <MenuItem
                  icon={<RiDeleteBinFill />}
                  color={colors.appBrick}
                  onClick={() => {
                    setCurrentlyDeletingItem(row?.original?.id ?? '')
                    openDeleteDialog()
                  }}
                >
                  {t('delete', 'Delete')}
                </MenuItem>
              )}
            </MenuList>
          </Menu>
        ),
      },
    ]

    return columns
  }, [user])

  const currentlyEditingFormValues = useMemo<
    ExtensionPreset | undefined
  >(() => {
    if (
      currentlyEditingItem === '' ||
      getLoading ||
      getExtensionsRequest.error
    ) {
      return undefined
    }

    return getApiResponse?.filter(data => data.id === currentlyEditingItem)?.[0]
  }, [getApiResponse, currentlyEditingItem, getExtensionsRequest, getLoading])

  const currentlyDeletingFormValues = useMemo<
    ExtensionPreset | undefined
  >(() => {
    if (
      currentlyDeletingItem === '' ||
      getLoading ||
      getExtensionsRequest.error
    ) {
      return undefined
    }

    return getApiResponse?.filter(
      data => data.id === currentlyDeletingItem,
    )?.[0]
  }, [getApiResponse, currentlyDeletingItem, getExtensionsRequest, getLoading])

  /*
  Add-ons
  */
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      data: filteredExtensions,
      columns: reactTableColumns,
    })

  /*
    Final render
  */
  return (
    <>
      <VStack alignItems="start" marginTop="1rem" spacing="0" width="full">
        <Box marginBottom="20px" width="full">
          <Box display="flex" gap={3} alignItems="center">
            <InputGroup flex={1} maxW="400px">
              <InputLeftElement pointerEvents="none">
                <FiSearch color={colors.appBattleshipGrey} />
              </InputLeftElement>
              <Input
                placeholder={t(
                  'search_saved_extensions',
                  'Search by name or description',
                )}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </InputGroup>

            <Spacer />

            <PermissionGuard
              permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
            >
              {(hasPermission: boolean) =>
                hasPermission ? (
                  <Button onClick={openFormModal}>+ Add New</Button>
                ) : (
                  <></>
                )
              }
            </PermissionGuard>
          </Box>
        </Box>

        <Table {...getTableProps()}>
          <Thead bgColor="appPaleGreyTwo">
            {headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <Th {...column.getHeaderProps()}>
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <PermissionGuard
            permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) => (
              <Tbody {...getTableBodyProps()}>
                {!getLoading ? (
                  rows.length > 0 ? (
                    rows.map(row => {
                      prepareRow(row)
                      return row.id === 'actions' && !hasPermission ? (
                        <></>
                      ) : (
                        <Tr {...row.getRowProps()}>
                          {row.cells.map(cell => {
                            return (
                              <Td maxW="20em" {...cell.getCellProps()}>
                                {cell.render('Cell')}
                              </Td>
                            )
                          })}
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={3} textAlign="center">
                        <ChakraText>
                          {searchText
                            ? t(
                                'no_saved_extensions_found',
                                'No saved extensions found',
                              )
                            : t(
                                'no_saved_extensions_yet',
                                'No saved extensions added so far',
                              )}
                        </ChakraText>
                      </Td>
                    </Tr>
                  )
                ) : (
                  <Tr>
                    <Td colSpan={3} textAlign="center">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                      />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            )}
          </PermissionGuard>
        </Table>
      </VStack>

      {formModalOpen ? (
        <FormModal
          isOpen={formModalOpen}
          onClose={() => {
            closeFormModal()
            setCurrentlyEditingItem('')
          }}
          onSuccess={() => {
            void getExtensions()
          }}
          initialValues={currentlyEditingFormValues}
          readOnly={
            !checkPermission({
              user,
              permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
            })
          }
        />
      ) : null}

      <DeleteDialog
        isOpen={deleteDialogOpen}
        onClose={() => {
          closeDeleteDialog()
          setCurrentlyDeletingItem('')
        }}
        onDelete={deleteExtensionPreset}
        extensionName={currentlyDeletingFormValues?.name ?? ''}
        isLoading={deleteLoading}
      />
    </>
  )
}

export default SavedExtensionsSettings
